import { useEffect, useState, useRef } from "react";
import { Situation, SituationInstance } from "../../../types/ai_pivot/situationTypes";
import { createSituationInstance, getLatestXSituationSummaries, getSituation } from "../../../service/situationService";
import { useParams } from "react-router";
import {
    IonCard, IonCardHeader, IonCardTitle,
    IonCardSubtitle, IonCardContent, IonButton,
    useIonToast, IonText, IonAlert, IonModal, IonHeader, IonToolbar,
    IonTitle, IonContent, IonList, IonItem, IonLabel, IonFooter, IonIcon
} from "@ionic/react";
import Picture from "./Picture";
import axios from 'axios';
import './SituationComponent.css';
import { useAuth } from "../../../context/AuthContext";
import Loading from "../../generic/Loading";
import Feedback from "./Feedback";
import DailyIframe, { DailyCall, DailyEventObjectTrack } from "@daily-co/daily-js";
import { supabase } from "../../../service/authService";
import PlanUpgradeComponent from "../payment/PlanUpgradeComponent";
import LikeDislikeComponent from './LikeDislikeComponent';
import RecordingAudio from '../situation/RecordingAudio';
import SurveyModal from '../situation/SurveyModal';
import { hourglass, play, stop, chatbubbleEllipsesOutline, micOutline } from "ionicons/icons";
import TextChatComponent from '../situation/TextChatComponent';


interface InstructionDetails {
    title: string;
    steps: string[];
    // imageUrl: string;
}

const SituationComponent: React.FC<{
    preloadedSituation?: Situation,
    handleMarkCompleted?: () => void
}> = ({ preloadedSituation, handleMarkCompleted }) => {

    const situationId = useParams<{ id: string }>().id;

    const [situation, setSituation] = useState<Situation>();
    const [inProgress, setInProgress] = useState(false);
    const [backgroundAudio, setBackgroundAudio] = useState<HTMLAudioElement>();
    const [present] = useIonToast();
    const [isLoading, setIsLoading] = useState(false);
    const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
    const { user, userProfile } = useAuth();
    const [callObject, setCallObject] = useState<DailyCall | null>(null);
    const [situationInstance, setSituationInstance] = useState<SituationInstance>();
    const [showPurchaseOptions, setShowPurchaseOptions] = useState<boolean>(false);
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [showRecordings, setShowRecordings] = useState(false);
    const [roomName, setRoomName] = useState<string>('');
    const [instanceId, setInstanceId] = useState<number>(1);

    const [showPreSurvey, setShowPreSurvey] = useState(false);
    const [showPostSurvey, setShowPostSurvey] = useState(false);
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const preSurveyResponseRef = useRef<{ [key: string]: string }>({});
    const postSurveyResponseRef = useRef<{ [key: string]: string }>({});
    const [startCalled, setStartCalled] = useState(false);

    const [useTextChat, setUseTextChat] = useState(false);
    const [showTextChat, setShowTextChat] = useState(false);
    const [postData, setPostData] = useState<{
        instance_id: number;
        prompt: string;
        feedback_prompt: string;
    } | null>(null);

    function getBrowserAndOS() {
        const userAgent = navigator.userAgent;
        console.log(userAgent)
        const isMac = userAgent.includes('Macintosh');
        const isiPhone = userAgent.includes('iPhone');
        const isWindows = userAgent.includes('Windows');
        const isAndroid = userAgent.includes('Android');
        const isChrome = userAgent.includes('Chrome');
        const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');

        if (isMac) {
            return isSafari ? 'Mac - Safari' : 'Mac - Chrome';
        } else if (isiPhone) {
            return isSafari ? 'iPhone - Safari' : 'iPhone - Chrome';
        } else if (isWindows) {
            return 'Windows - Chrome';
        } else if (isAndroid) {
            return 'Android - Chrome';
        } else {
            return 'Other';
        }
    }


    useEffect(() => {
        const unwrapSituation = (data: Situation) => {
            setSituation(data);
            if (data.audio_url && data.audio_volume) {
                const audio = new Audio(data.audio_url);
                audio.loop = true;
                console.log(data.audio_volume);
                audio.volume = data.audio_volume; //data.audio_volume;
                setBackgroundAudio(audio);
            }
        }

        const fetchSituation = async () => {
            const { data, error } = await getSituation(situationId);
            if (error) {
                console.error('error fetching situation: ', JSON.stringify(error));
            } else if (data) {
                unwrapSituation(data);
            }
        }

        if (preloadedSituation) {
            unwrapSituation(preloadedSituation);
        } else if (situationId) {
            fetchSituation();
        } 

        return () => {
            console.log('Stopping conversation');
            handleStop();
        }
    }, []);

    useEffect(() => {
        if (!situation) return;


        const call = DailyIframe.createCallObject({
            audioSource: true,
            videoSource: false,
            subscribeToTracksAutomatically: true
            // subscribeToTracksAutomatically: true
        });
        setCallObject(call);

        call.on('participant-joined', participant => {
            console.log('[participant-joined]', participant);
        });
        call.on('participant-left', participant => {
            console.log('[participant-left]', participant);
        });
        call.on('track-started', playAudio);
        call.on('track-stopped', (e) => console.log('[track-stopped]', e));

        return () => {
            call.leave();
            call.destroy();
        };
    }, [situation]);

    const playAudio = (event: DailyEventObjectTrack) => {
        console.log('Track started event:', event);


        if (!event.participant || !event.track || event.participant.local) {
            console.log('Ignoring track: either participant or track is null, or it\'s the local participant\'s track.');
            return;
        }

        if (event.track.kind !== 'audio') {
            console.log('Ignoring non-audio track');
            return;
        }

        let audioEl = document.querySelector(`audio[data-participant-id='${event.participant.session_id}']`) as HTMLAudioElement;
        if (!audioEl) {
            console.log('Creating new audio element for participant:', event.participant.session_id);
            audioEl = document.createElement('audio');
            audioEl.setAttribute('data-participant-id', event.participant.session_id);
            audioEl.autoplay = true;
            document.body.appendChild(audioEl);
        }

        console.log('Attaching the track and playing it');
        audioEl.srcObject = new MediaStream([event.track]);
        audioEl.play().catch((error) => {
            console.error('Error playing the audio:', error);
            handlePermissionIssue();
        });
    };

    const handlePresurvey = async () => {
        preSurveyResponseRef.current = {};
        // setStartCalled(false);
        setShowPreSurvey(true);
    }

    const showPrompt = () => {
        present({
            header: "Let's get started!",
            message: 'AI might lag and be unresponsive at times - we do everything we can to ensure you have the best possible experience',
            position: 'middle',
            buttons: [
                {
                    text: 'Okay',
                    role: 'info',
                    handler: () => {
                        setStartCalled(false);
                        handlePresurvey();
                    },
                }
            ]
        });
    }

    const getAdditionalContext = async () => {

        if (situation && user) {
            const { data, error } = await getLatestXSituationSummaries(situation, user.id);
            if (error) {
                console.error('Error loading last therapy meeting summaries');
            } else if (data) {
                let res = "";
                // first loop to check if there is feedback recorded and initialize additional context
                for (let i = 0; i < data.length; i++) {
                    let currFeedback = data[i].feedback_as_text;
                    if (currFeedback && currFeedback.length > 0 && res.length === 0) {
                        res = `
Additional instructions: 
- Review previous session summary notes below. If there were planned action items, start by asking the user if he/she took action noted during previous sessions.

Context:
Previous session summary notes: `
                    }
                }

                // second loop to add previous session summary notes
                for (let i = 0; i < data.length; i++) {
                    let currFeedback = data[i].feedback_as_text;
                    if (currFeedback && currFeedback.length > 0) {
                        res += `

''' ${currFeedback}' '''`
                    }
                }

                return res;
            }
        }

        return "";
    }


    const handleStart = async (useTextChatValue: boolean) => {
        console.log("state",useTextChatValue)
        console.log("situation",situation)
        if (useTextChatValue  && situation) {
            try {
                setIsLoading(true);
                const { data, error } = await createSituationInstance(situation.id);
                if (error || !data) {
                    if (error?.message.includes('You ran out of available sessions.')) {
                        setShowPurchaseOptions(true);
                    } else {
                        console.error("Failed to initialize situation instance:", error);
                        alert('Error encountered, please try again later');
                    }
                    setIsLoading(false);
                    return;
                }
                const instanceId = data.id;
                let additionalContext = await getAdditionalContext();
                let completePrompt = situation.prompt + additionalContext;
                completePrompt = completePrompt.replace('{name}', userProfile?.name || 'User');
                const postData = {
                    instance_id: instanceId,
                    prompt: completePrompt,
                    feedback_prompt: situation.feedback_prompt
                };
                setShowTextChat(true);
                setInProgress(true);
                setPostData(postData);
                setInstanceId(instanceId);
                setSituationInstance(data);
            } catch (error) {
                console.error('Error starting the chat:', error);
                alert('Error encountered while starting the chat. Please reload the page and retry.');
            } finally {
                setIsLoading(false);
            }
            return;
        }


        console.log("Alert")
        setShowRecordings(false);
        try {
            const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            switch (permissionStatus.state) {
                case 'granted':
                    startCall();
                    break;
                case 'prompt':
                    try {
                        await navigator.mediaDevices.getUserMedia({ audio: true });
                        startCall();
                    } catch (error) {
                        console.error('Microphone access denied by user:', error);
                        handlePermissionIssue();
                    }
                    break;
                case 'denied':
                    handlePermissionIssue();
                    break;
            }
        } catch (error) {
            console.error('Error checking microphone permissions:', error);
            handlePermissionIssue();
        }
    };

    const startCall = async () => {
        console.log("Starting call...")
        if (situation && callObject && user && userProfile && situation.id !== undefined) {
            setIsLoading(true);

            const { data, error } = await createSituationInstance(situation.id);
            if (error || !data) {
                if (error?.message.includes('You ran out of available sessions.')) {
                    setShowPurchaseOptions(true);
                } else {
                    console.error("Failed to initialize situation instance:", error);
                    alert('Error encountered, please try again later');
                }
                setIsLoading(false);
                return;
            }
            // console.log(data);
            setSituationInstance(data);

            let additionalContext = await getAdditionalContext();
            console.log('additinal context: ', additionalContext);
            let completePrompt = situation.prompt + additionalContext;
            completePrompt = completePrompt.replace('{name}', userProfile.name);
            console.log('complete prompt: ', completePrompt);

            const postData = {
                instance_id: data.id,
                prompt: completePrompt,
                voice_id: situation.voice_id,
                feedback_prompt: situation.feedback_prompt
            };
            try {
                console.log(postData);
                console.log("Hello")
                const response = await axios.post(process.env.NODE_ENV === 'development' ? 'https://finalbackend-nuob.onrender.com/start' : 'https://finalbackend-nuob.onrender.com/start', postData);
                console.log("H", response)
                if (response.data && response.data.url) {
                    console.log('Joining Daily room:', response.data.url);
                    setRoomName(response.data.room_name);
                    setInstanceId(data.id);
                    await callObject.join({ url: response.data.url });
                    await callObject.startRecording({
                        layout: {
                            preset: 'audio-only',
                        },
                    });
                    if (backgroundAudio)
                        backgroundAudio.play();
                    setInProgress(true);

                    // update ballance
                    // const { error } = await supabase
                    //         .from('users')
                    //         .update({balance: userProfile.balance - 1})
                    //         .eq('userid', userProfile.userid);
                    if (error) {
                        console.error('error decreasing user balance: ', JSON.stringify(error));
                    }
                }
            } catch (error) {
                console.error('Error starting the call:', error);
                alert('Error encountered while starting the call. Please reload the page and retry.');
            }
            setIsLoading(false);
        }
    };

    const handlePermissionIssue = () => {
        console.log("test")
        const browserAndOS = getBrowserAndOS();
        const instructions = getInstructionByBrowser(browserAndOS);
        setModalContent(`Microphone access is required to start the session. ${instructions}`);
        setShowModal(true);

        handleStop(); //Verify on future 
        setIsFeedbackLoading(false); //Verify on future 
        setIsLoading(false); //Verify on future 
    };

    const getInstructionByBrowser = (browserAndOS: string): InstructionDetails => {
        const instructionsMap: { [key: string]: InstructionDetails } = {
            'Mac - Safari': {
                title: 'Enable Microphone Access in Safari on Mac',
                steps: [
                    'Open Safari.',
                    'Click on Safari in the menu bar, then select Preferences.',
                    'Navigate to the Websites tab.',
                    'Scroll to the Microphone section.',
                    'Next to the website you want to allow, change the setting to Allow.'
                ],
                // imageUrl: '/path_to_safari_mac_image.png'
            },
            'Mac - Chrome': {
                title: 'Enable Microphone Access in Chrome on Mac',
                steps: [
                    'Open Chrome.',
                    'Click on Chrome in the menu bar, then select Preferences.',
                    'Scroll to the Privacy and Security section, and click on Site Settings.',
                    'Find and click on Microphone.',
                    'Make sure the setting is set to Ask before accessing or Allow for specific sites.'
                ],
                // imageUrl: '/path_to_chrome_mac_image.png'
            },
            'iPhone - Safari': {
                title: 'Enable Microphone Access in Safari on iPhone',
                steps: [
                    'Open the Settings app.',
                    'Scroll down and tap Safari.',
                    'Scroll down to the Privacy & Security section.',
                    'Tap on Microphone and ensure it’s set to Allow.'
                ],
                // imageUrl: '/path_to_safari_iphone_image.png'
            },
            'iPhone - Chrome': {
                title: 'Enable Microphone Access in Chrome on iPhone',
                steps: [
                    'Open the Chrome app.',
                    'Tap on the menu at the bottom-right (three dots).',
                    'Tap on Settings.',
                    'Scroll down to Site Settings.',
                    'Tap on Microphone and change it to Ask first or Allow.'
                ],
                // imageUrl: '/path_to_chrome_iphone_image.png'
            },
            'Windows - Chrome': {
                title: 'Enable Microphone Access in Chrome on Windows',
                steps: [
                    'Open Chrome.',
                    'Click on the three dots in the upper right corner to open the menu.',
                    'Click on Settings.',
                    'Scroll down and click on Privacy and security.',
                    'Click on Site settings.',
                    'Under Permissions, click on Microphone.',
                    'Ensure that Ask before accessing is on, or allow specific sites to use the microphone.'
                ],
                // imageUrl: '/path_to_chrome_windows_image.png'
            },
            'Android - Chrome': {
                title: 'Enable Microphone Access in Chrome on Android',
                steps: [
                    'Open the Chrome app.',
                    'Tap on the menu icon (three dots) at the top right.',
                    'Tap on Settings.',
                    'Scroll down to Site settings.',
                    'Tap on Microphone and ensure access is allowed.'
                ],
                // imageUrl: '/path_to_chrome_android_image.png'
            },
            'Other': {
                title: 'Enable Microphone Access',
                steps: [
                    'Open your browser.',
                    'Navigate to the browser’s settings or preferences.',
                    'Find the privacy or security section.',
                    'Locate microphone settings and enable it for the required website.'
                ],
                // imageUrl: '/generic_browser_settings_image.png'
            }
        };

        return instructionsMap[browserAndOS] || instructionsMap['Other'];
    };

    useEffect(() => {
        if (situationInstance) {
            console.log('situation instance is present: ', situationInstance);
            const channel = supabase
                .channel('schema-db-changes')
                .on(
                    'postgres_changes',
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'situation_instance',
                        filter: `id=eq.${situationInstance.id}`,
                    },
                    (payload) => {
                        let si = payload.new as SituationInstance;
                        if (si.feedback_as_text && isFeedbackLoading) {
                            setIsFeedbackLoading(false);
                        }
                        setSituationInstance(payload.new as SituationInstance)
                    }
                )
                .subscribe();
            return () => {
                channel.unsubscribe();
            }
        }
    }, [situationInstance?.id])


    const handleStop = () => {
        if (callObject) {
            callObject.leave();
        }
        setInProgress(false);
        setIsFeedbackLoading(true);
        setShowRecordings(true);
        setShowPostSurvey(true);
        setSurveyCompleted(false);
        backgroundAudio?.pause();
        setShowTextChat(false);
        console.log('Call ended');
    };

    const updateSurveyResponse = async () => {
        console.log("Updating Supabase with survey responses...");
        console.log(preSurveyResponseRef.current);
        console.log(postSurveyResponseRef.current);

        const isPublic = preSurveyResponseRef.current['make_public'] === 'true';

        const { error } = await supabase
            .from('situation_instance')
            .update({
                survey_response: {
                    preSurvey: preSurveyResponseRef.current,
                    postSurvey: postSurveyResponseRef.current
                },
                public: isPublic
            })
            .eq('id', instanceId);

        if (error) {
            console.error('Error updating survey responses in Supabase:', error);
        } else {
            console.log('Survey responses successfully updated in Supabase');
        }
    };

    const PermissionModal = () => {
        const instructions = getInstructionByBrowser(getBrowserAndOS());
        const onClose = () => setShowModal(false);
        return (
            <IonModal isOpen={showModal} onDidDismiss={onClose}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle style={{ textAlign: 'center' }}>Enable Microphone Access</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <div style={{ fontWeight: 'bold', fontSize: '1.2em', marginBottom: '10px' }}>
                        {instructions.title}
                    </div>
                    <IonList>
                        {instructions.steps.map((step, index) => (
                            <IonItem key={index} lines="none">
                                <IonLabel>
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                        <div style={{ fontWeight: 'bold', marginRight: '8px' }}>{index + 1}.</div>
                                        <div>{step}</div>
                                    </div>
                                </IonLabel>
                            </IonItem>
                        ))}
                    </IonList>
                    {/* {instructions.imageUrl && (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <img src={instructions.imageUrl} alt="Visual Guide" style={{ width: '100%', maxWidth: '500px', padding: '10px' }} />
                        </div>
                    )} */}
                    <IonButton expand="block" onClick={onClose} style={{ marginTop: '20px' }}>Close</IonButton>
                </IonContent>
            </IonModal>
        );
    };

    return (
        <div>
            <PermissionModal />
            <PlanUpgradeComponent show={showPurchaseOptions} close={() => setShowPurchaseOptions(false)} />
            <IonCard mode="ios" className="customm-ion-card">
                <IonCardHeader>
                    <IonCardSubtitle>{situation?.description}</IonCardSubtitle>
                    <br />
                    {/* <IonText>
                        <div>
                            @{situation?.author_username}
                        </div>
                    </IonText> */}
                    <IonCardTitle>{situation?.title}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                    {situation &&
                        <div style={{ width: '100%', height: '100%', backgroundColor: 'danger' }}
                            onClick={() => {
                                if (situation) {
                                    if (!isLoading) {
                                        if (!inProgress) {
                                            showPrompt();
                                        } else {
                                            handleStop();
                                        }
                                    }
                                }
                            }
                            }
                        >
                            <IonIcon style={
                                {
                                    position: 'absolute',
                                    zIndex: 2,
                                    left: 0,
                                    right: 0,
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    top: 0,
                                    bottom: 0,
                                    marginTop: 'auto',
                                    marginBottom: 'auto',
                                    color: 'white',
                                    width: '50px',
                                    height: '50px'
                                }} icon={isLoading ? hourglass : inProgress ? stop : play} size="large" />
                            <Picture image_url={situation.image_url} />
                        </div>
                    }
                </IonCardContent>
                <div className="action-btn-container">
                    {/* {isLoading ? <IonText color="primary"><h4>Get ready, loading...</h4></IonText> : <>
                        {!inProgress ?
                            <IonButton onClick={showPrompt} expand="block">
                                Start
                            </IonButton> :
                            <IonButton color="danger" onClick={handleStop} expand="block">
                                Stop
                            </IonButton>
                        }
                    </>} */}
                </div>
            </IonCard>

            {showRecordings && <RecordingAudio roomName={roomName} InstanceId={instanceId} />}

            <IonCard mode="ios" className="custom-ion-card">
                <IonCardHeader>
                    <IonCardTitle>Summary</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                    {situationInstance && situationInstance.feedback_as_text ?
                        <Feedback feedback={situationInstance.feedback_as_text} /> : isFeedbackLoading
                            ? <Loading />
                            : 'Summary will be generated after the session'}
                </IonCardContent>

            </IonCard>

            <SurveyModal
                isOpen={showPreSurvey}
                justCloseModal={() => setShowPreSurvey(false)}
                onClose={(data) => {
                    setShowPreSurvey(false);
                    console.log('Pre-call survey data:', data);
                    if (data) {
                        preSurveyResponseRef.current = data;
                        const useTextChatValue = data.useTextChat === 'true';
                        setUseTextChat(useTextChatValue);
                        console.log('useTextChat state updated:', useTextChatValue);
                        setSurveyCompleted(true);
                        if (!startCalled) {
                            setStartCalled(true);
                            handleStart(useTextChatValue);
                        }
                    }
                }}
                title="Pre-call Survey"
                questions={[
                    { id: 'q1', text: 'What are you afraid might happen?', type: 'text' },
                    { id: 'make_public', text: 'Get feedback from others?', type: 'boolean' },
                    { id: 'useTextChat', text: 'Use text chat?', type: 'toggle' }
                ]}
            />
            <SurveyModal
                isOpen={showPostSurvey}
                onClose={(data) => {
                    setShowPostSurvey(false);
                    console.log('Post-call survey data:', data);
                    if (data) {
                        handleMarkCompleted?.();
                        postSurveyResponseRef.current = data;
                        updateSurveyResponse();
                        console.log("Alert2")
                    }
                }}
                title="Post-call Survey"
                questions={[
                    { id: 'q5', text: 'Anxiety level', type: 'range' },
                    { id: 'q6', text: 'How realistic / engaging did it feel?', type: 'range' },
                    { id: 'q1', text: 'Did the thing that you were afraid of actually happened and why do you think so?', type: 'text' },
                    { id: 'q2', text: 'If what you feared happened, was it as bad as you thought?', type: 'text' },
                    { id: 'q3', text: 'What have you learned?', type: 'text' },
                    { id: 'q4', text: 'Any feedback for this scenario?', type: 'text' }
                ]}
            />
            {showTextChat && postData && situation &&(
                <TextChatComponent
                    postData={postData}
                    situationData={{
                        title: situation.title,
                        description: situation.description,
                        author_username: situation.author_username,
                        version: situation.version,
                        image_url: situation.image_url
                    }}
                    isOpen={showTextChat}
                    onClose={() => {
                        setShowTextChat(false);
                        handleStop();
                    }}
                />
            )}
        </div>
    );
}

export default SituationComponent;